import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import HTMLHead from 'HtmlHead';
import ProtectedRoute from 'components/Navigation/ProtectedRoute';
import Order from 'components/Orders/Order';
import ReturnStatus from 'components/ReturnStatus/ReturnStatus';
import BatchReturnStatus from 'components/batch-returns/BatchReturnStatus';
import BatchReturnDetail from 'components/batch-returns/BatchReturnDetail';
import Layout from 'components/Returns/Layout';
import { StyledToastContainer } from 'components/ui/Toast';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AppContextIdName } from 'returnCenter-api';
import styled, { ThemeProvider } from 'styled-components';
import Select, { SingleValue } from 'react-select';
import FontStyle from 'styles/fonts';
import { GlobalStyle } from 'styles/globalstyles';
import { ReactDatepicker } from 'styles/reactDatepicker';
import Spacing from 'styles/spacing';
import defaultTheme from 'theme/defaultTheme';
import { CustomNavigationClient } from './utils/NavigationClient';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useLanguage } from 'hooks/useLanguage';
import {
    companiesSelector as selector,
    companyThunks,
} from 'store/reducers/companiesSlice';
import { companyChanged } from 'store/actions/companyActions';
import Home from 'components/Home/Home';
import { ContentContainer } from 'components/ui/ContentContainer';
import Admin from 'components/Admin/Admin';
import { SelectOption } from 'types/Option';
import Flex from 'components/ui/Flex';
import BatchReturnHome from 'components/batch-returns/BatchReturn';

function ClientSideNavigation({ pca, children }: { pca: PublicClientApplication; children: JSX.Element }) {
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    const [firstRender, setFirstRender] = useState(true);
    useEffect(() => {
        setFirstRender(false);
    }, []);

    if (firstRender) return null;

    return children;
}

const AppContent = ({ pca, companies }: { pca: PublicClientApplication; companies: AppContextIdName[] }) => {
    const { lang, changeLanguage, availableLanguages, getLanguageLabel } = useLanguage();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const currentCompany = useAppSelector(selector.selectCurrentCompany);
    const siteSettings = useAppSelector(selector.selectDisplaySettings);

    const companyOptions = companies.map((company) => ({
        value: company.marketId != null ? `${company.id}|${company.marketId}` : company.id ?? '',
        label: company.name ?? '',
    }));

    const languageOptions = availableLanguages.map((lng) => ({
        value: lng,
        label: getLanguageLabel(lng),
    }));

    const handleCompanyChange = (selectedOption: SingleValue<SelectOption>) => {
        if (!selectedOption) return;
        const value = selectedOption.value as string;
        const [contextId, marketIdStr] = value.split('|');
        const marketId = marketIdStr ? Number(marketIdStr) : undefined;
        dispatch(companyChanged({ contextId, marketId }));
        navigate(`/`);
        dispatch(companyThunks.getAppDisplaySettings());
    };

    const handleLanguageChange = (selectedOption: SingleValue<SelectOption>) => {
        if (!selectedOption) return;
        changeLanguage(selectedOption.value as string);
    };

    return (
        <ClientSideNavigation pca={pca}>
            <MsalProvider instance={pca}>
                <HTMLHead />
                <ContentContainer>
                    <Flex gap="0.5rem" className="select-container pl-2 pt-2 pb-2">
                        <StyledSelect
                            options={companyOptions}
                            value={companyOptions.find((opt) => {
                                const key = currentCompany?.marketId != null
                                    ? `${currentCompany.id}|${currentCompany.marketId}`
                                    : currentCompany?.id ?? '';
                                return opt.value === key;
                            })}
                            onChange={handleCompanyChange}
                        />

                        <StyledSelect
                            options={languageOptions}
                            value={languageOptions.find((opt) => opt.value === lang)}
                            onChange={handleLanguageChange}
                        />
                    </Flex>
                </ContentContainer>
                <ThemeProvider theme={defaultTheme}>
                    <GlobalStyle />
                    <FontStyle />
                    <Spacing />
                    <ReactDatepicker siteSettings={siteSettings} />
                    <StyledToastContainer position="top-center" newestOnTop draggable={false} autoClose={false} />
                    <Routes>
                        <Route path="/" element={<Layout />}>
                            <Route
                                index
                                element={
                                    <ProtectedRoute>
                                        <Home />
                                    </ProtectedRoute>
                                }
                            />

                            <Route path="search/" element={<Navigate to="/" replace />} />

                            <Route
                                path="search/order/:id"
                                element={
                                    <ProtectedRoute>
                                        <Order />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="returns/:status"
                                element={
                                    <ProtectedRoute>
                                        <ReturnStatus />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="returns/:status/:id"
                                element={
                                    <ProtectedRoute>
                                        <Order />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="batch-returns"
                                element={
                                    <ProtectedRoute>
                                        <BatchReturnHome />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="batch-returns/:status/:id"
                                element={
                                    <ProtectedRoute>
                                        <BatchReturnDetail />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="batch-returns/:status"
                                element={
                                    <ProtectedRoute>
                                        <BatchReturnStatus />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin"
                                element={
                                    <ProtectedRoute>
                                        <Admin />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Route>
                    </Routes>
                </ThemeProvider>
            </MsalProvider>
        </ClientSideNavigation>
    );
};

export default AppContent;

const StyledSelect = styled(Select<SelectOption>)`
    min-width: 150px;
`;
